import React, { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabase'

const inputStyle = {
  background: 'var(--input-bg)', border: '1px solid var(--border)',
  borderRadius: '4px', padding: '0.55rem 0.75rem',
  fontSize: '0.88rem', fontFamily: 'DM Sans, sans-serif',
  color: 'var(--text)', width: '100%', boxSizing: 'border-box',
  outline: 'none', transition: 'border-color 0.18s'
}

const labelStyle = {
  display: 'block', fontFamily: 'monospace',
  fontSize: '0.67rem', fontWeight: '500',
  textTransform: 'uppercase', letterSpacing: '0.08em',
  color: 'var(--text-muted)', marginBottom: '0.35rem'
}

export default function Profile({ session, profile, onProfileUpdate }) {
  const [form, setForm] = useState({
    full_name: '', phone_number: '', photo_url: '',
    address_line1: '', address_line2: '', city: '', state: '', pincode: ''
  })
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [status, setStatus] = useState(null)
  const fileInputRef = useRef(null)

  useEffect(() => {
    if (profile) {
      setForm({
        full_name: profile.full_name || '',
        phone_number: profile.phone_number || '',
        photo_url: profile.photo_url || '',
        address_line1: profile.address_line1 || '',
        address_line2: profile.address_line2 || '',
        city: profile.city || '',
        state: profile.state || '',
        pincode: profile.pincode || ''
      })
    }
  }, [profile])

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  async function handlePhotoUpload(e) {
    const file = e.target.files[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setStatus({ ok: false, msg: 'Please select an image file.' })
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setStatus({ ok: false, msg: 'Image must be under 5 MB.' })
      return
    }

    setUploading(true)
    setStatus(null)

    const ext = file.name.split('.').pop()
    const path = `${session.user.id}/avatar.${ext}`

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(path, file, { upsert: true, contentType: file.type })

    if (uploadError) {
      setUploading(false)
      setStatus({ ok: false, msg: 'Upload failed: ' + uploadError.message })
      return
    }

    const { data } = supabase.storage.from('avatars').getPublicUrl(path)
    const publicUrl = data.publicUrl + '?t=' + Date.now()
    set('photo_url', publicUrl)
    setUploading(false)
    setStatus({ ok: true, msg: 'Photo uploaded. Click Save Profile to apply.' })
  }

  async function handleSave() {
    setSaving(true)
    setStatus(null)
    const { error } = await supabase
      .from('profiles')
      .upsert({
        id: session.user.id,
        full_name: form.full_name.trim() || null,
        phone_number: form.phone_number.trim() || null,
        photo_url: form.photo_url.trim() || null,
        address_line1: form.address_line1.trim() || null,
        address_line2: form.address_line2.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        pincode: form.pincode.trim() || null,
      })
    setSaving(false)
    if (error) {
      setStatus({ ok: false, msg: error.message })
    } else {
      setStatus({ ok: true, msg: 'Profile saved successfully.' })
      if (onProfileUpdate) onProfileUpdate()
    }
  }

  const displayName = form.full_name || session?.user?.user_metadata?.full_name || session?.user?.email || 'User'
  const avatarUrl = form.photo_url || session?.user?.user_metadata?.avatar_url
  const initials = displayName.charAt(0).toUpperCase()

  const addressParts = [form.address_line1, form.address_line2, form.city, form.state, form.pincode].filter(Boolean)

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto' }}>

      {/* Profile card */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', marginBottom: '1.5rem' }}>
        <div style={{ background: 'var(--surface-hdr)', borderBottom: '1px solid var(--border)', padding: '1rem 1.5rem' }}>
          <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Your Profile</span>
        </div>
        <div style={{ padding: '1.5rem', display: 'flex', alignItems: 'flex-start', gap: '1.2rem' }}>
          <div style={{ flexShrink: 0 }}>
            {avatarUrl
              ? <img src={avatarUrl} alt="avatar" style={{ width: '64px', height: '64px', borderRadius: '50%', border: '2px solid var(--border)', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none' }} />
              : <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'var(--btn-bg)', color: 'var(--btn-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', fontWeight: '700', fontFamily: 'Georgia,serif' }}>{initials}</div>
            }
          </div>
          <div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.1rem', fontWeight: '700', marginBottom: '0.2rem' }}>{displayName}</div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text-muted)' }}>{session?.user?.email}</div>
            {form.phone_number && (
              <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>{form.phone_number}</div>
            )}
            {addressParts.length > 0 && (
              <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.1rem', lineHeight: 1.5 }}>
                {addressParts.join(', ')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit form */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ background: 'var(--surface-hdr)', borderBottom: '1px solid var(--border)', padding: '1rem 1.5rem' }}>
          <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Edit Profile</span>
        </div>

        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          {status && (
            <div style={{
              padding: '0.7rem 1rem', borderRadius: '6px', fontSize: '0.82rem',
              background: status.ok ? 'rgba(74,103,65,0.08)' : 'rgba(184,68,26,0.08)',
              border: `1px solid ${status.ok ? 'rgba(74,103,65,0.25)' : 'rgba(184,68,26,0.2)'}`,
              color: status.ok ? '#4a6741' : '#b8441a'
            }}>
              {status.ok ? '✓ ' : '⚠ '}{status.msg}
            </div>
          )}

          {/* Personal */}
          <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', paddingBottom: '0.3rem', borderBottom: '1px solid var(--surface-hdr)' }}>
            Personal Details
          </div>

          <div>
            <label style={labelStyle}>Full Name</label>
            <input style={inputStyle} placeholder="e.g. Rahul Sharma" value={form.full_name}
              onChange={e => set('full_name', e.target.value)}
              onFocus={e => e.target.style.borderColor = 'var(--text)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'} />
          </div>

          <div>
            <label style={labelStyle}>Phone Number</label>
            <input style={inputStyle} placeholder="e.g. +91 98765 43210" value={form.phone_number}
              onChange={e => set('phone_number', e.target.value)}
              onFocus={e => e.target.style.borderColor = 'var(--text)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'} />
          </div>

          {/* Photo upload */}
          <div>
            <label style={labelStyle}>Profile Photo</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {avatarUrl
                ? <img src={avatarUrl} alt="preview" style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', border: '1.5px solid var(--border)', flexShrink: 0 }} onError={e => { e.target.style.display = 'none' }} />
                : <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'var(--surface-hdr)', border: '1.5px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'var(--text-muted)', fontSize: '1.1rem' }}>?</div>
              }
              <div style={{ flex: 1 }}>
                <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
                <button
                  onClick={() => fileInputRef.current.click()}
                  disabled={uploading}
                  style={{
                    background: 'none', border: '1px solid var(--border)',
                    padding: '0.45rem 0.9rem', borderRadius: '4px',
                    fontFamily: 'monospace', fontSize: '0.7rem', cursor: uploading ? 'not-allowed' : 'pointer',
                    color: 'var(--text)', transition: 'all 0.15s', opacity: uploading ? 0.6 : 1
                  }}
                  onMouseEnter={e => { if (!uploading) e.target.style.borderColor = 'var(--text)' }}
                  onMouseLeave={e => e.target.style.borderColor = 'var(--border)'}
                >
                  {uploading ? 'Uploading...' : '↑ Upload Photo'}
                </button>
                <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
                  JPG, PNG, WebP · max 5 MB
                </div>
              </div>
            </div>
          </div>

          {/* Address */}
          <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', paddingBottom: '0.3rem', borderBottom: '1px solid var(--surface-hdr)', marginTop: '0.5rem' }}>
            Address
          </div>

          <div>
            <label style={labelStyle}>Address Line 1</label>
            <input style={inputStyle} placeholder="House / Flat no., Building, Street" value={form.address_line1}
              onChange={e => set('address_line1', e.target.value)}
              onFocus={e => e.target.style.borderColor = 'var(--text)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'} />
          </div>

          <div>
            <label style={labelStyle}>Address Line 2</label>
            <input style={inputStyle} placeholder="Area, Locality (optional)" value={form.address_line2}
              onChange={e => set('address_line2', e.target.value)}
              onFocus={e => e.target.style.borderColor = 'var(--text)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
            <div>
              <label style={labelStyle}>City</label>
              <input style={inputStyle} placeholder="e.g. Mumbai" value={form.city}
                onChange={e => set('city', e.target.value)}
                onFocus={e => e.target.style.borderColor = 'var(--text)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </div>
            <div>
              <label style={labelStyle}>State</label>
              <input style={inputStyle} placeholder="e.g. Maharashtra" value={form.state}
                onChange={e => set('state', e.target.value)}
                onFocus={e => e.target.style.borderColor = 'var(--text)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </div>
          </div>

          <div style={{ maxWidth: '160px' }}>
            <label style={labelStyle}>Pincode</label>
            <input style={inputStyle} placeholder="e.g. 400001" value={form.pincode}
              onChange={e => set('pincode', e.target.value)}
              onFocus={e => e.target.style.borderColor = 'var(--text)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'} />
          </div>

        </div>

        <div style={{ padding: '0.9rem 1.5rem 1.3rem', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleSave}
            disabled={saving || uploading}
            style={{
              background: 'var(--btn-bg)', color: 'var(--btn-text)', border: 'none',
              padding: '0.5rem 1.4rem', borderRadius: '4px',
              fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: '500',
              cursor: (saving || uploading) ? 'not-allowed' : 'pointer',
              textTransform: 'uppercase', letterSpacing: '0.05em',
              opacity: (saving || uploading) ? 0.6 : 1, transition: 'opacity 0.15s'
            }}
          >
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </div>

    </div>
  )
}
